import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon, Settings as SettingsIcon } from '@mui/icons-material';
import {
  useGetGameSettingsQuery,
  useUpdateGameSettingsMutation,
} from '../../store/api/authApi';

const emptySettings = {
  singlePlayerHouse: '',
  multiPlayer: { heads: '', tails: '', house: '' },
  jackpotAllocation: '',
  minBet: '',
  maxBet: '',
};

const multiPlayerFields = [
  { key: 'heads', label: 'Heads %' },
  { key: 'tails', label: 'Tails %' },
  { key: 'house', label: 'House %' },
];

const splitNonHouse = (house) => {
  const safeHouse = Math.max(0, Math.min(100, Number(house) || 0));
  const nonHouse = Math.round((100 - safeHouse) * 100) / 100;
  const heads = Math.round((nonHouse / 2) * 100) / 100;
  const tails = Math.round((nonHouse - heads) * 100) / 100;
  return { heads, tails, house: Math.round(safeHouse * 100) / 100 };
};

const formatForForm = (settings) => ({
  singlePlayerHouse: settings?.singlePlayer?.house ?? '',
  multiPlayer: {
    heads: settings?.multiPlayer?.heads ?? '',
    tails: settings?.multiPlayer?.tails ?? '',
    house: settings?.multiPlayer?.house ?? '',
  },
  jackpotAllocation: settings?.jackpotAllocation ?? '',
  minBet: settings?.minBet ?? '',
  maxBet: settings?.maxBet ?? '',
});

const parseSettings = (settings) => ({
  singlePlayer: splitNonHouse(settings.singlePlayerHouse),
  multiPlayer: {
    heads: Number(settings.multiPlayer.heads),
    tails: Number(settings.multiPlayer.tails),
    house: Number(settings.multiPlayer.house),
  },
  jackpotAllocation: Number(settings.jackpotAllocation),
  minBet: Number(settings.minBet),
  maxBet: Number(settings.maxBet),
});

const totalFor = (group = {}) =>
  Number(group.heads || 0) + Number(group.tails || 0) + Number(group.house || 0);

const AdminSettings = () => {
  const [form, setForm] = useState(emptySettings);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const { data, isLoading, isFetching, error } = useGetGameSettingsQuery();
  const [updateGameSettings, { isLoading: isSaving }] = useUpdateGameSettingsMutation();

  useEffect(() => {
    if (data) {
      setForm(formatForForm(data));
    }
  }, [data]);

  const multiPlayerTotal = useMemo(
    () => totalFor(form.multiPlayer),
    [form.multiPlayer]
  );

  const handleChange = (section, field, value) => {
    setFeedback({ type: '', message: '' });

    if (!field) {
      setForm((prev) => ({
        ...prev,
        [section]: value,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = parseSettings(form);
    const singlePlayerHouse = Number(form.singlePlayerHouse);
    const invalidSinglePlayerHouse =
      !Number.isFinite(singlePlayerHouse) ||
      singlePlayerHouse < 0 ||
      singlePlayerHouse > 100;
    const invalidMultiPlayer = Object.values(payload.multiPlayer).some(
      (value) => !Number.isFinite(value) || value < 0 || value > 100
    );
    const invalidJackpotAllocation =
      !Number.isFinite(payload.jackpotAllocation) ||
      payload.jackpotAllocation < 0 ||
      payload.jackpotAllocation > 100;
    const invalidBetLimits =
      !Number.isFinite(payload.minBet) ||
      !Number.isFinite(payload.maxBet) ||
      payload.minBet <= 0 ||
      payload.maxBet <= 0 ||
      payload.minBet > payload.maxBet;

    if (invalidSinglePlayerHouse || invalidMultiPlayer || invalidJackpotAllocation) {
      setFeedback({
        type: 'error',
        message: 'All percentage values must be numbers between 0 and 100.',
      });
      return;
    }

    if (invalidBetLimits) {
      setFeedback({
        type: 'error',
        message: 'Min/max play amounts must be greater than 0, and min cannot exceed max.',
      });
      return;
    }

    if (Math.abs(totalFor(payload.multiPlayer) - 100) > 0.01) {
      setFeedback({
        type: 'error',
        message: 'Multi-player percentages must total 100%.',
      });
      return;
    }

    try {
      const saved = await updateGameSettings(payload).unwrap();
      setForm(formatForForm(saved));
      setFeedback({
        type: 'success',
        message: 'Game settings saved successfully.',
      });
    } catch (saveError) {
      setFeedback({
        type: 'error',
        message: saveError?.data?.message || 'Failed to save game settings.',
      });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Game Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Control play amounts and outcome percentages for single-player and multi-player rounds.
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Play Amount Limits
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Minimum Play Amount (K)"
                    type="number"
                    inputProps={{ min: 0.01, max: 1_000_000, step: 'any' }}
                    value={form.minBet}
                    onChange={(event) => handleChange('minBet', null, event.target.value)}
                    helperText="Smallest stake a player can place (web + USSD)."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Maximum Play Amount (K)"
                    type="number"
                    inputProps={{ min: 0.01, max: 1_000_000, step: 'any' }}
                    value={form.maxBet}
                    onChange={(event) => handleChange('maxBet', null, event.target.value)}
                    helperText="Largest stake a player can place (web + USSD)."
                  />
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Jackpot Allocation
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Jackpot Allocation %"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                value={form.jackpotAllocation}
                onChange={(event) => handleChange('jackpotAllocation', null, event.target.value)}
                helperText="Percentage of the total pool moved into jackpot when the house wins."
              />
            </Stack>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error">
            {error?.data?.message || 'Failed to load game settings.'}
          </Alert>
        )}

        {feedback.message && (
          <Alert severity={feedback.type || 'info'}>
            {feedback.message}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Single Player
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="House %"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    value={form.singlePlayerHouse}
                    onChange={(event) =>
                      handleChange('singlePlayerHouse', null, event.target.value)
                    }
                    helperText="Fixed house-win chance. Heads/tails are randomized each round from the remaining percentage."
                  />

                  <Alert severity="info">
                    Heads and tails are redrawn every solo round (about 40–60% of the non-house pool each). Only house % is configurable.
                  </Alert>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Multi Player
                    </Typography>
                  </Box>

                  {multiPlayerFields.map((field) => (
                    <TextField
                      key={field.key}
                      fullWidth
                      label={field.label}
                      type="number"
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      value={form.multiPlayer[field.key]}
                      onChange={(event) =>
                        handleChange('multiPlayer', field.key, event.target.value)
                      }
                    />
                  ))}

                  <Alert severity={Math.abs(multiPlayerTotal - 100) <= 0.01 ? 'success' : 'warning'}>
                    Total: {multiPlayerTotal.toFixed(2)}%
                  </Alert>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={isSaving || isFetching}
          >
            Save Settings
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default AdminSettings;
